import { useState } from "react";
import React from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { classNames, htmlValidation, useStore } from "../helpers";
import { useTranslation } from "react-i18next";
import { theme } from "./DashboardPage";
import { DeleteModal, LoadingState } from "../components";
import { Button, Stack } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { AddNewProject } from "./add-new-project";
import { useFilteredProjects, useIsMobile } from "../custom-hooks";
import { Skeleton } from "@mui/lab";
import { useLocalStorage } from "react-use";
import { safe_get } from "../report-visuals";
import { Vortex } from "react-loader-spinner";

const Projects = observer(() => {
    const rootStore = useStore();
    const { userStore, projectStore, voterListStore, digitalArchiveStore } = rootStore;
    const [projects, setProjects] = useState([]);
    const navigate = useNavigate();
    const [t] = useTranslation("");
    const [searchQuery, setSearchQUery] = useState("");
    const isMobile = useIsMobile();
    const isRoot = userStore.isRoot();
    const [value] = useLocalStorage("__userprofile", {});
    const projectsLength = safe_get(value, "projects", []);
    const { isLoading, refetch, isRefetching } = useQuery({
        queryKey: ["projects-list"],
        queryFn: async () => await userStore.getProjects(),
        onSuccess: (data) => {
            setProjects(data);
        },
    });

    const deleteProject = async (evt, id) => {
        htmlValidation(evt);
        const res = await projectStore.deleteProject({}, id);
        if (res.status === 200) {
            toast.warn("Project deleted successfully");
        } else toast.error("Something went wrong");
        setOnDeleteProjectClick(false);
        refetch();
    };

    const cancelCB = (evt) => {
        htmlValidation(evt);
        setOnDeleteProjectClick(false);
    };

    const [onDeleteProjectClick, setOnDeleteProjectClick] = useState(false);
    const [activeProject, setActiveProject] = useState({});

    // const handle_project_change = async (project) => {
    //   const parts = window.location.pathname.split("/");
    //   const idx = parts.indexOf("project") + 1;
    //   parts[idx] = project["id"];
    //   const newPath = parts.join("/");
    //   navigate(newPath);
    //   await localStorage.setItem("isSigned", "no");
    //   rootStore.update_switch_project(false);
    //   // await digitalArchiveStore.update_folder_breadcrumbs([]);
    //   // await digitalArchiveStore.update_DAStoreProp("activeParentId", "")
    //
    //   // //TODO: update project id in voter filters
    //   // const clone_voter_filters = clone(voterListStore.voter_filters);
    //   // clone_voter_filters["projectid"] = parseInt(project["id"])
    //   // await voterListStore.update_filters(clone_voter_filters);
    // };

    const handle_project_change = async (project) => {
        projectStore.update_active_project(project);

        localStorage.setItem("__active_project", JSON.stringify(project));

        navigate(`/project/${project.id}`);

        rootStore.update_switch_project(false);
    };

    const handleEditClick = (e, project) => {
        htmlValidation(e);
        projectStore.set_addNewProjectModal(true);
        projectStore.update_active_project(project);
    };

    const handleDeleteClick = (e, project) => {
        htmlValidation(e);
        setOnDeleteProjectClick(true);
        setActiveProject(project);
    };

    const { filteredProjects } = useFilteredProjects({ projects, searchQuery });

    return (
        <>
            {isLoading || isRefetching ? (
                <Stack spacing={1}>
                    {Array(projectsLength.length)
                        .fill("projects")
                        .map((val, i) => (
                            <Skeleton key={i} variant="rectangular" height={60} />
                        ))}
                </Stack>
            ) : (
                <>
                    <div className="mt-2 flex w-full flex-col gap-3 text-gray-500 sm:flex-row sm:items-center sm:justify-between">
                        {/* Search Input */}
                        <input
                            value={searchQuery}
                            onChange={(evt) => setSearchQUery(evt.target.value)}
                            className="w-full flex-1 rounded-full border border-gray-300 bg-white px-4 py-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 sm:w-auto sm:text-base"
                            type="search"
                            name="search"
                            placeholder="Search Projects"
                        />

                        {isRoot && (
                            <Button
                                variant="contained"
                                onClick={() => {
                                    projectStore.set_addNewProjectModal(true);
                                    projectStore.update_active_project({});
                                }}
                                endIcon={<i className="fas fa-plus-circle" />}
                                className="w-full !rounded-full font-semibold tracking-wide shadow-sm sm:w-auto"
                                sx={{
                                    backgroundColor: "#1E66F5",
                                    "&:hover": { backgroundColor: "#1558D6" },
                                    textTransform: "none",
                                    fontSize: "0.95rem",
                                }}
                            >
                                Add New Project
                            </Button>
                        )}
                    </div>

                    <div className="mt-5 flex flex-col">
                        {filteredProjects.map((project) => {
                            return (
                                <div
                                    key={project.id}
                                    className="group my-2 overflow-hidden rounded-xl bg-white shadow-md transition-all duration-200 hover:shadow-lg"
                                    style={{
                                        borderLeft: `4px solid ${theme}`,
                                        borderTopLeftRadius: 0,
                                        borderBottomLeftRadius: 0,
                                    }}
                                >
                                    <div
                                        onClick={() => handle_project_change(project)}
                                        className="flex cursor-pointer items-center justify-between px-5 py-4"
                                    >
                                        {/* Left */}
                                        <div className="min-w-0 flex-1">
                                            <h3 className="truncate text-lg font-medium text-gray-900">
                                                {project.name}
                                            </h3>
                                        </div>

                                        {/* Right */}
                                        <div className="ml-6 flex items-center gap-4">
                                            {/* Date */}
                                            {/*<div className="flex items-center rounded-full bg-gray-100 px-4 py-1.5 text-sm font-medium text-gray-600">*/}
                                            {/*    {project.timestamp || project.dates}*/}
                                            {/*</div>*/}

                                            {/*{(project.timestamp || project.dates) && (*/}
                                            {/*    <div className="flex items-center rounded-full bg-gray-100 px-4 py-1.5 text-sm font-medium text-gray-600">*/}
                                            {/*        {project.timestamp || project.dates}*/}
                                            {/*    </div>*/}
                                            {/*)}*/}

                                            {/* Hover Actions */}
                                            {isRoot && (
                                                <div
                                                    className={classNames(
                                                        isMobile
                                                            ? "flex"
                                                            : "opacity-0 group-hover:opacity-100",
                                                        "flex items-center gap-4 transition-all duration-200"
                                                    )}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <button
                                                        onClick={(e) => handleEditClick(e, project)}
                                                        className="text-sm font-medium text-blue-600 hover:text-blue-800"
                                                    >
                                                        Edit
                                                    </button>

                                                    <button
                                                        onClick={(e) => handleDeleteClick(e, project)}
                                                        className="text-sm font-medium text-red-500 hover:text-red-700"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            <AddNewProject />
            <DeleteModal
                onDeleteClick={deleteProject}
                onCancelClick={cancelCB}
                isOpen={onDeleteProjectClick}
                id={activeProject["id"]}
                desc={`Do you want to Delete ${activeProject["name"]} Project ?`}
            />
        </>
    );
});

export default React.memo(Projects);
